import React, { useState } from "react";
import styled from "styled-components";
import { skills } from "../../data/constants";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  background-color: transparent;
  padding: 40px 16px;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.white};
  margin-bottom: 2rem;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: -0.02em;
  align-self: flex-start;

  &::before {
    content: "~/ ";
    color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 40px;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// VS Code Mockup Layout
const EditorWindow = styled.div`
  width: 100%;
  max-width: 900px;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  display: flex;
  height: 520px;
  font-family: 'JetBrains Mono', monospace;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    min-height: 600px;
  }
`;

// Sidebar File Explorer
const Sidebar = styled.div`
  width: 240px;
  background: #252526;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #333;
  }
`;

const SidebarTitle = styled.div`
  padding: 10px 16px;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #888;
  font-weight: bold;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #2d2d2d;
`;

const ExplorerTree = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FolderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #cccccc;
  padding: 4px 6px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${props => props.$active ? '#8bc34a' : '#aaa'};
  background: ${props => props.$active ? '#37373d' : 'transparent'};
  padding: 6px 12px;
  margin-left: 14px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #e0e0e0;
    background: #2d2d2d;
  }
`;

// Main Editor Pane
const EditorPane = styled.div`
  flex-grow: 1;
  background: #1e1e1e;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TabsBar = styled.div`
  display: flex;
  background: #2d2d2d;
  border-bottom: 1px solid #252526;
  overflow-x: auto;
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$active ? '#1e1e1e' : 'transparent'};
  border-right: 1px solid #252526;
  font-size: 0.85rem;
  color: ${props => props.$active ? '#8bc34a' : '#888'};
  cursor: pointer;

  &:hover {
    color: #e0e0e0;
  }
`;

const TabClose = styled.span`
  font-size: 0.75rem;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const EditorContent = styled.div`
  padding: 24px;
  flex-grow: 1;
  overflow-y: auto;
  text-align: left;
  display: flex;
  flex-direction: column;
  color: #85c2f2;
`;

const JsonOuter = styled.div`
  color: #d4d4d4;
  font-size: 0.85rem;
  margin-bottom: 8px;
`;

const JsonBrace = styled.span`
  color: #ffd700;
`;

const JsonKey = styled.span`
  color: #9cdcfe;
`;

// Grid of visually glowing badges inside editor
const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 16px;
  padding: 16px 24px;
  width: 100%;
`;

const SkillBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #151515;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 14px;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.$color};
    box-shadow: 0 0 15px ${props => props.$color}80;
  }
`;

const SkillLogo = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const SkillName = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: #cccccc;
  text-align: center;
  font-family: 'Space Grotesk', sans-serif;
  
  ${SkillBadge}:hover & {
    color: #ffffff;
  }
`;

// Map names to official brand colors for glows
const getBrandColor = (name) => {
  const mapping = {
    "React Js": "#61dafb",
    "Redux": "#764abc",
    "HTML": "#e34f26",
    "CSS": "#1572b6",
    "JavaScript": "#f7df1e",
    "Tailwind CSS": "#06b6d4",
    "Node Js": "#339933",
    "Express Js": "#888888",
    "FastAPI": "#009688",
    "Django": "#092e20",
    "Django REST Framework": "#a30000",
    "Rest APIs": "#005b96",
    "MySQL": "#4479a1",
    "MongoDB": "#47a248",
    "PostgreSQL": "#336791",
    "C": "#a8b9cc",
    "C++": "#00599c",
    "Python": "#3776ab",
    "JAVA": "#f89820",
    "Git": "#f05032",
    "GitHub": "#ffffff",
    "VS Code": "#007acc",
    "Postman": "#ff6c37",
  };
  return mapping[name] || "#8bc34a"; // Fallback to Spring Green
};

const Skills = () => {
  // Use state to track which category file is active
  const [activeTab, setActiveTab] = useState("Frontend");

  // Get active skills list
  const activeSkillsList = skills.find(s => s.title === activeTab)?.skills || [];

  return (
    <Container id="Skills">
      <Wrapper>
        <SectionTitle>SkillsWorkspace.code-workspace</SectionTitle>
        <Desc>
          Click categories in the Explorer tree to inspect my capabilities inside my VS Code mockup.
        </Desc>

        <EditorWindow>
          {/* VS Code Side Folder Explorer */}
          <Sidebar>
            <SidebarTitle>Explorer</SidebarTitle>
            <ExplorerTree>
              <FolderItem>
                <span style={{ color: "#ffd700" }}>📂</span> src
              </FolderItem>
              <FolderItem style={{ marginLeft: "14px" }}>
                <span style={{ color: "#ffd700" }}>📂</span> skills
              </FolderItem>
              {skills.map((category) => (
                <FileItem
                  key={category.title}
                  $active={activeTab === category.title}
                  onClick={() => setActiveTab(category.title)}
                >
                  <span style={{ color: "#8bc34a" }}>📄</span> {category.title.toLowerCase()}.json
                </FileItem>
              ))}
            </ExplorerTree>
          </Sidebar>

          {/* Main Editor Console Area */}
          <EditorPane>
            {/* Tabs top list */}
            <TabsBar>
              <Tab $active={true}>
                <span>📄</span> {activeTab.toLowerCase()}.json
                <TabClose>×</TabClose>
              </Tab>
            </TabsBar>

            {/* Content inside json editor mockup */}
            <EditorContent>
              <JsonOuter>
                <JsonBrace>{"{"}</JsonBrace>
              </JsonOuter>
              <JsonOuter style={{ marginLeft: "16px" }}>
                <JsonKey>"{activeTab.toLowerCase()}"</JsonKey>: <JsonBrace>{"["}</JsonBrace>
              </JsonOuter>

              {/* Badges Layout */}
              <SkillsGrid>
                {activeSkillsList.map((skill) => (
                  <SkillBadge key={skill.name} $color={getBrandColor(skill.name)}>
                    <SkillLogo src={skill.image} alt={skill.name} />
                    <SkillName>{skill.name}</SkillName>
                  </SkillBadge>
                ))}
              </SkillsGrid>

              <JsonOuter style={{ marginLeft: "16px" }}>
                <JsonBrace>{"]"}</JsonBrace>
              </JsonOuter>
              <JsonOuter>
                <JsonBrace>{"}"}</JsonBrace>
              </JsonOuter>
            </EditorContent>
          </EditorPane>
        </EditorWindow>
      </Wrapper>
    </Container>
  );
};

export default Skills;
